import type { Ref, MaybeRefOrGetter } from 'vue'

export function preserveWidthWhile (component: Ref<any>, loading: MaybeRefOrGetter<any>) {
  watch(loading, (loading) => {
    const el: HTMLElement = component.value.$el
    el.style.minWidth = loading ? `${el.offsetWidth}px` : ''
  })
}

export function useVisualViewport () {
  if (import.meta.env.SSR) return

  const visualViewport = ref(Math.round(window.visualViewport?.height || window.innerHeight))
  const viewportOffsetTop = ref(Math.round(window.visualViewport?.offsetTop || 0))

  const isKeyboardOpen = computed(() => !!window.visualViewport && visualViewport.value < window.innerHeight)

  const keyboardHeight = computed(() => Math.round(window.innerHeight - visualViewport.value))

  let rafId: number | null = null
  const listener = () => {
    // rAF throttle: scroll fires many times per frame; only update refs once per paint cycle.
    if (rafId !== null) return
    rafId = requestAnimationFrame(() => {
      visualViewport.value = Math.round(window.visualViewport?.height || window.innerHeight)
      viewportOffsetTop.value = Math.round(window.visualViewport?.offsetTop || 0)
      rafId = null
    })
  }

  onMounted(() => {
    // passive: true lets the browser scroll immediately without waiting for this handler to finish.
    window.visualViewport?.addEventListener('resize', listener, { passive: true })
    window.visualViewport?.addEventListener('scroll', listener, { passive: true })
  })

  onUnmounted(() => {
    window.visualViewport?.removeEventListener('resize', listener)
    window.visualViewport?.removeEventListener('scroll', listener)
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  })

  return {
    visualViewport,
    viewportOffsetTop,
    keyboardHeight,
    isKeyboardOpen,
  }
}

export function preventWindowScroll ({ when }: { when: () => boolean }) {
  if (import.meta.env.SSR) return

  let lockedScrollY = 0
  let isLocked = false

  const preventScroll = () => {
    const shouldLock = when()

    if (shouldLock && !isLocked) {
      // Just became locked - save current position
      lockedScrollY = window.scrollY
      isLocked = true
    }
    else if (!shouldLock && isLocked) {
      // Just became unlocked
      isLocked = false
    }

    if (isLocked)
      window.scrollTo({ left: 0, top: lockedScrollY, behavior: 'instant' })
  }

  onMounted(() => {
    window.addEventListener('scroll', preventScroll)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', preventScroll)
  })
}
